import { defineComponent, ref, VNodeRef, watch, toRef, toRaw, WatchStopHandle, onMounted } from 'vue';
import { isElementOrChidOf } from '../../../../platform/core/element-extensions';
import { DeadlineFormField } from './deadline-form-field';
import { isPastDate } from '@/platform/core/date-extensions';

export default defineComponent({
	props: {
		modelValue: DeadlineFormField,
	},
	emits: ['editorBlurred'],
	setup: (props, context) => {
		const container = ref<HTMLElement>();
		const isEnabledAnytime = ref<boolean>(true);
		const isEnabledNotSpecified = ref<boolean>(true);
		const isEnabledSpecified = ref<boolean>(true);
		const isEnabledDate = ref<boolean>(true);

		const disabledDate = (date: Date): boolean => {
			return isPastDate(date);
		};

		const onFocusOut = (e: FocusEvent): void => {
			if (container.value && !isElementOrChidOf(container.value, e.relatedTarget)) {
				context.emit('editorBlurred');
			}
		};

		const adjustEnabledFields = (): void => {
			isEnabledAnytime.value = isEnabledNotSpecified.value = isEnabledSpecified.value = isEnabledDate.value = false;

			const formField = toRaw(props.modelValue);
			if (formField !== undefined) {
				isEnabledAnytime.value = formField.noSelectionMade() || formField.isAnytime.value;
				isEnabledNotSpecified.value = formField.noSelectionMade() || formField.isNotSpecified.value;
				isEnabledSpecified.value = formField.noSelectionMade() || formField.isSpecified.value;
				isEnabledDate.value = formField.isSpecified.value;
			}
		};

		const guardSelectionConsistency = (): void => {
			const formField = toRaw(props.modelValue);
			if (formField === undefined) {
				return;
			}

			if (!formField.isSpecified.value) {
				formField.date.value = undefined;
			}
		};

		let selectionChangeWatchStopHandle: WatchStopHandle | undefined = undefined;
		const maintainSelectionChangeWatch = (): void => {
			if (selectionChangeWatchStopHandle) {
				selectionChangeWatchStopHandle();
			}

			const formField = toRaw(props.modelValue);
			if (formField === undefined) {
				return;
			}

			selectionChangeWatchStopHandle = watch(
				[formField.isAnytime, formField.isNotSpecified, formField.isSpecified, formField.date],
				() => {
					adjustEnabledFields();
					guardSelectionConsistency();
				}
			);
		};

		watch(toRef(props, 'modelValue'), () => maintainSelectionChangeWatch());

		onMounted(() => {
			maintainSelectionChangeWatch();
			adjustEnabledFields();
			guardSelectionConsistency();
		});

		return {
			isEnabledAnytime,
			isEnabledNotSpecified,
			isEnabledSpecified,
			isEnabledDate,
			onFocusOut,
			disabledDate,
			templateRefContainer: (el: VNodeRef | undefined): void => {
				container.value = el as HTMLElement | undefined;
			},
		};
	},
});
